/**
 * Browser auto-refresh installer.
 *
 * Wires `refreshAuth` to common "user is here" signals — visibility change,
 * window focus, bfcache restore, and a periodic interval — so apps that share
 * the JWT cookie (Gitea, etc.) don't get logged out mid-session while active.
 *
 * Designed to be bundled as a self-contained IIFE for injection into
 * non-bundled hosts. See `scripts/build-browser-bundles.mjs`.
 */

import { refreshAuth, type CreateAuthRefreshOptions } from './auth.js';

export interface InstallAuthRefreshOptions extends CreateAuthRefreshOptions {
  /** Minimum gap between refresh attempts (ms). Default 60s. */
  minIntervalMs?: number;
  /** Periodic refresh tick while page is visible (ms). Default 10min. 0 disables. */
  periodicMs?: number;
}

export interface AuthRefreshHandle {
  /** Trigger a refresh now (subject to min-interval throttling). */
  refresh: () => Promise<boolean>;
  /** Remove all listeners and stop the periodic tick. */
  uninstall: () => void;
}

const DEFAULT_MIN_INTERVAL_MS = 60_000;
const DEFAULT_PERIODIC_MS = 10 * 60_000;

export function installAuthRefresh(options?: InstallAuthRefreshOptions): AuthRefreshHandle {
  const minIntervalMs = options?.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;
  const periodicMs = options?.periodicMs ?? DEFAULT_PERIODIC_MS;

  let lastRefresh = 0;
  let inflight: Promise<boolean> | null = null;

  const trigger = async (): Promise<boolean> => {
    const now = Date.now();
    if (inflight) return inflight;
    if (now - lastRefresh < minIntervalMs) return false;

    lastRefresh = now;
    inflight = refreshAuth(options).finally(() => { inflight = null; });
    return inflight;
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') void trigger();
  };
  const onFocus = () => { void trigger(); };
  const onPageShow = (e: PageTransitionEvent) => {
    if (e.persisted) void trigger();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onFocus);
  window.addEventListener('pageshow', onPageShow);

  let timer: ReturnType<typeof setInterval> | null = null;
  if (periodicMs > 0) {
    timer = setInterval(() => {
      if (document.visibilityState === 'visible') void trigger();
    }, periodicMs);
  }

  return {
    refresh: trigger,
    uninstall: () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      if (timer) clearInterval(timer);
    },
  };
}
